/** @file Per-vertex blended skinning affine Σ wᵢ·(Wᵢ·Bᵢ⁻¹) of a skinned mesh at the setup pose (§6.7). */
import type { Id, MeshAtt } from '@pose2d/format/types';
import { throwError } from '@pose2d/format/throwError';
import { affine } from '@pose2d/math/affine';
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import { invertChecked } from './invertChecked';

type Skinned = Extract<MeshAtt['geometry'], { kind: 'skinned' }>;
const STRIDE = POSE_LAYOUT.WORLD_STRIDE;

/** WORLD_STRIDE numbers per vertex: Σ w·(worldOf(b)·bindPose[b]⁻¹); `path` (`attachments.<id>.geometry`) locates bind errors. */
export function skinBlend(g: Skinned, worldOf: (bone: Id) => Float64Array, path: string): Float64Array {
  const perBone = new Map<Id, Float64Array>();
  const skinOf = (bone: Id): Float64Array => {
    const known = perBone.get(bone);
    if (known) return known;
    const bind = g.bindPose[bone] ?? throwError('VALIDATION', undefined, { path: `${path}.bindPose`, hint: `weighted bone ${bone} has no bindPose entry` });
    const k = new Float64Array(STRIDE);
    affine.mul(k, worldOf(bone), invertChecked(bind, `${path}.bindPose.${bone}`, 'the bind pose is singular'));
    perBone.set(bone, k);
    return k;
  };
  const out = new Float64Array(g.weights.length * STRIDE);
  g.weights.forEach((influences, v) => {
    for (const [bone, w] of influences) {
      const k = skinOf(bone);
      for (let i = 0; i < STRIDE; i++) out[v * STRIDE + i] = (out[v * STRIDE + i] ?? 0) + w * (k[i] ?? 0);
    }
  });
  return out;
}
